/**
 * Vue Currency Input 2.0.0-beta.2
 * (c) 2018-2021 Matthias Stiller
 * @license MIT
 */
import { ref, getCurrentInstance, isVue3, computed, onMounted, onUnmounted } from 'vue-demi';

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const removeLeadingZeros = (str) => str.replace(/^0+(0$|[^0])/, '$1');
const count = (str, search) => (str.match(new RegExp(escapeRegExp(search), 'g')) || []).length;
const startsWith = (str, search) => str.substring(0, search.length) === search;
const substringBefore = (str, search) => str.substring(0, str.indexOf(search));

const DECIMAL_SYMBOLS = [',', '.', '٫'];
const INTEGER_PATTERN = '(0|[1-9]\\d*)';
class NumberFormat {
  constructor (options) {
    const { currency, locale, precision, autoDecimalDigits } = options;
    const numberFormat = new Intl.NumberFormat(locale, { currency, style: 'currency' });
    const ps = numberFormat.format(123456);
    this.locale = locale;
    this.currency = currency;
    this.digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => i.toLocaleString(locale));
    this.decimalSymbol = count(ps, this.digits[0]) ? ps.substr(ps.indexOf(this.digits[6]) + 1, 1) : undefined;
    this.groupingSymbol = ps.substr(ps.indexOf(this.digits[3]) + 1, 1);
    this.minusSymbol = substringBefore(Number(-1).toLocaleString(locale), this.digits[1]);
    if (this.decimalSymbol === undefined) {
      this.minimumFractionDigits = this.maximumFractionDigits = 0;
    } else if (typeof precision === 'number') {
      this.minimumFractionDigits = this.maximumFractionDigits = precision;
    } else if (typeof precision === 'object' && !autoDecimalDigits) {
      this.minimumFractionDigits = precision.min || 0;
      this.maximumFractionDigits = precision.max !== undefined ? precision.max : 20;
    } else {
      this.minimumFractionDigits = numberFormat.resolvedOptions().minimumFractionDigits;
      this.maximumFractionDigits = numberFormat.resolvedOptions().maximumFractionDigits;
    }
    this.prefix = substringBefore(ps, this.digits[1]);
    this.negativePrefix = substringBefore(numberFormat.format(-1), this.digits[1]);
    this.suffix = ps.substring(ps.lastIndexOf(this.decimalSymbol ? this.digits[0] : this.digits[6]) + 1);
  }
  parse (str) {
    if (str) {
      const negative = this.isNegative(str);
      str = this.normalizeDigits(str);
      str = this.stripCurrencySymbol(str);
      str = this.stripMinusSymbol(str);
      const fraction = this.decimalSymbol ? `(?:${escapeRegExp(this.decimalSymbol)}(\\d*))?` : '';
      const match = this.stripGroupingSymbol(str).match(new RegExp(`^${INTEGER_PATTERN}${fraction}$`));
      if (match && this.isValidIntegerFormat(str.split(this.decimalSymbol)[0], Number(match[1]))) {
        return Number(`${negative ? '-' : ''}${(this.onlyDigits(match[1]))}.${(this.onlyDigits(match[2] || ''))}`)
      }
    }
    return null
  }
  isValidIntegerFormat (formattedNumber, number) {
    return [
      this.normalizeDigits(number.toLocaleString(this.locale, { useGrouping: true })),
      this.normalizeDigits(number.toLocaleString(this.locale, { useGrouping: false }))
    ].includes(formattedNumber)
  }
  format (number, options = {
    minimumFractionDigits: this.minimumFractionDigits,
    maximumFractionDigits: this.maximumFractionDigits
  }) {
    return number.toLocaleString(this.locale, {
      style: 'currency',
      currency: this.currency,
      ...options
    })
  }
  toFraction (str) {
    return `${this.digits[0]}${this.decimalSymbol}${(this.onlyLocaleDigits(str.substr(1)).substr(0, this.maximumFractionDigits))}`
  }
  isFractionIncomplete (str) {
    return !!this.normalizeDigits(this.stripGroupingSymbol(str)).match(new RegExp(`^${INTEGER_PATTERN}${escapeRegExp(this.decimalSymbol)}$`))
  }
  isNegative (str) {
    return startsWith(str, this.negativePrefix) || startsWith(str.replace('-', this.minusSymbol), this.minusSymbol)
  }
  insertCurrencySymbol (str, negative) {
    return `${negative ? this.negativePrefix : this.prefix}${str}${this.suffix}`
  }
  stripGroupingSymbol (str) {
    return str.replace(new RegExp(escapeRegExp(this.groupingSymbol), 'g'), '')
  }
  stripMinusSymbol (str) {
    return str.replace('-', this.minusSymbol).replace(this.minusSymbol, '')
  }
  stripCurrencySymbol (str) {
    return str.replace(this.negativePrefix, '').replace(this.prefix, '').replace(this.suffix, '')
  }
  normalizeDecimalSymbol (str, from) {
    DECIMAL_SYMBOLS.forEach((s) => {
      str = str.substr(0, from) + str.substr(from).replace(s, this.decimalSymbol);
    });
    return str
  }
  normalizeDigits (str) {
    if (this.digits[0] !== '0') {
      this.digits.forEach((digit, index) => {
        str = str.replace(new RegExp(digit, 'g'), index);
      });
    }
    return str
  }
  onlyDigits (str) {
    return this.normalizeDigits(str).replace(/\D+/g, '')
  }
  onlyLocaleDigits (str) {
    return str.replace(new RegExp(`[^${this.digits.join('')}]*`, 'g'), '')
  }
}

class DefaultNumberMask {
  constructor (numberFormat) {
    this.numberFormat = numberFormat;
  }
  conformToMask (str, previousConformedValue = '') {
    const negative = this.numberFormat.isNegative(str);
    const checkIncompleteValue = (str) => {
      if (str === '' && negative && previousConformedValue !== this.numberFormat.negativePrefix) {
        return ''
      } else if (this.numberFormat.maximumFractionDigits > 0) {
        if (this.numberFormat.isFractionIncomplete(str)) {
          return str
        } else if (startsWith(str, this.numberFormat.decimalSymbol)) {
          return this.numberFormat.toFraction(str)
        }
      }
      return null
    };
    let value = str;
    value = this.numberFormat.stripCurrencySymbol(value);
    value = this.numberFormat.stripMinusSymbol(value);
    const incompleteValue = checkIncompleteValue(value);
    if (incompleteValue != null) {
      return this.numberFormat.insertCurrencySymbol(incompleteValue, negative)
    }
    const [integer, ...fraction] = value.split(this.numberFormat.decimalSymbol);
    const integerDigits = removeLeadingZeros(this.numberFormat.onlyDigits(integer));
    const fractionDigits = this.numberFormat.onlyDigits(fraction.join('')).substr(0, this.numberFormat.maximumFractionDigits);
    const invalidFraction = fraction.length > 0 && fractionDigits.length === 0;
    const invalidNegativeValue = integerDigits === '' && negative && (previousConformedValue === str.slice(0, -1) || previousConformedValue !== this.numberFormat.negativePrefix);
    if (invalidFraction || invalidNegativeValue) {
      return previousConformedValue
    } else if (integerDigits.match(/\d+/)) {
      return {
        numberValue: Number(`${negative ? '-' : ''}${integerDigits}.${fractionDigits}`),
        fractionDigits
      }
    } else {
      return ''
    }
  }
}
class AutoDecimalDigitsNumberMask {
  constructor (numberFormat) {
    this.numberFormat = numberFormat;
  }
  conformToMask (str) {
    if (str === '') {
      return ''
    }
    const negative = this.numberFormat.isNegative(str);
    const numberValue = this.numberFormat.stripMinusSymbol(str) === ''
      ? -0
      : Number(`${negative ? '-' : ''}${removeLeadingZeros(this.numberFormat.onlyDigits(str))}`) / Math.pow(10, this.numberFormat.minimumFractionDigits);
    return {
      numberValue,
      fractionDigits: numberValue.toFixed(this.numberFormat.minimumFractionDigits).slice(-this.numberFormat.minimumFractionDigits)
    }
  }
}

const MAX_SAFE_INTEGER = Math.pow(2, 53) - 1;
const DEFAULT_OPTIONS = {
  locale: undefined,
  currency: undefined,
  valueAsInteger: false,
  distractionFree: true,
  precision: undefined,
  autoDecimalDigits: false,
  valueRange: undefined,
  allowNegative: true,
  useGrouping: true
};
class NumberInput {
  constructor (el, options) {
    this.el = el;
    this.numberValue = null;
    this.addEventListener();
    this.init(options);
    this.setValue(this.currencyFormat.parse(this.el.value));
  }
  init (options) {
    const { distractionFree, autoDecimalDigits, valueRange, valueAsInteger, allowNegative, useGrouping } = { ...DEFAULT_OPTIONS, ...options };
    this.autoDecimalDigits = autoDecimalDigits;
    this.valueAsInteger = valueAsInteger;
    this.allowNegative = allowNegative;
    this.useGrouping = useGrouping;
    this.hideCurrencySymbolOnFocus = distractionFree === true || !!(distractionFree || {}).hideCurrencySymbol;
    this.hideNegligibleDecimalDigitsOnFocus = distractionFree === true || !!(distractionFree || {}).hideNegligibleDecimalDigits;
    this.hideGroupingSymbolOnFocus = distractionFree === true || !!(distractionFree || {}).hideGroupingSymbol;
    if (valueRange) {
      this.valueRange = {
        min: valueRange.min !== undefined ? Math.max(valueRange.min, -MAX_SAFE_INTEGER) : -MAX_SAFE_INTEGER,
        max: valueRange.max !== undefined ? Math.min(valueRange.max, MAX_SAFE_INTEGER) : MAX_SAFE_INTEGER
      };
    } else {
      this.valueRange = {
        min: -MAX_SAFE_INTEGER,
        max: MAX_SAFE_INTEGER
      };
    }
    if (autoDecimalDigits) {
      this.hideNegligibleDecimalDigitsOnFocus = false;
      this.el.setAttribute('inputmode', 'numeric');
    } else {
      this.el.setAttribute('inputmode', 'decimal');
    }
    this.currencyFormat = new NumberFormat(options);
    this.numberMask = autoDecimalDigits ? new AutoDecimalDigitsNumberMask(this.currencyFormat) : new DefaultNumberMask(this.currencyFormat);
  }
  setOptions (options) {
    this.init(options);
    this.applyFixedFractionFormat(this.numberValue, true);
  }
  dispatchEvent (eventName) {
    const event = document.createEvent('CustomEvent');
    event.initCustomEvent(eventName, true, true, { ...this.getValue() });
    this.el.dispatchEvent(event);
  }
  applyFixedFractionFormat (number, forcedChange) {
    this.format(number != null ? this.currencyFormat.format(this.validateValueRange(number)) : null);
    if (number !== this.numberValue || forcedChange) {
      this.dispatchEvent('change');
    }
  }
  getValue () {
    const numberValue = this.valueAsInteger && this.numberValue != null ? Number(this.numberValue.toFixed(this.currencyFormat.maximumFractionDigits).split('.').join('')) : this.numberValue;
    return {
      number: numberValue,
      formatted: this.formattedValue
    }
  }
  setValue (value) {
    const newValue = this.valueAsInteger && value != null ? value / Math.pow(10, this.currencyFormat.maximumFractionDigits) : value;
    if (newValue !== this.numberValue) {
      this.applyFixedFractionFormat(newValue);
    }
  }
  validateValueRange (value) {
    const { min, max } = this.valueRange;
    return Math.min(Math.max(value, min), max)
  }
  format (value, hideNegligibleDecimalDigits = false) {
    if (value != null) {
      if (this.decimalSymbolInsertedAt !== undefined) {
        value = this.currencyFormat.normalizeDecimalSymbol(value, this.decimalSymbolInsertedAt);
        this.decimalSymbolInsertedAt = undefined;
      }
      const conformedValue = this.numberMask.conformToMask(value, this.formattedValue);
      let formattedValue;
      if (typeof conformedValue === 'object') {
        const { numberValue, fractionDigits } = conformedValue;
        let { maximumFractionDigits, minimumFractionDigits } = this.currencyFormat;
        if (this.focus) {
          minimumFractionDigits = maximumFractionDigits;
        }
        minimumFractionDigits = hideNegligibleDecimalDigits
          ? fractionDigits.replace(/0+$/, '').length
          : Math.min(minimumFractionDigits, fractionDigits.length);
        formattedValue = numberValue > MAX_SAFE_INTEGER
          ? this.formattedValue
          : this.currencyFormat.format(numberValue, {
            useGrouping: this.useGrouping && !(this.focus && this.hideGroupingSymbolOnFocus),
            minimumFractionDigits,
            maximumFractionDigits
          });
      } else {
        formattedValue = conformedValue;
      }
      if (!this.allowNegative) {
        formattedValue = formattedValue.replace(this.currencyFormat.negativePrefix, this.currencyFormat.prefix);
      }
      if (this.focus && this.hideCurrencySymbolOnFocus) {
        formattedValue = formattedValue
          .replace(this.currencyFormat.negativePrefix, this.currencyFormat.minusSymbol)
          .replace(this.currencyFormat.prefix, '')
          .replace(this.currencyFormat.suffix, '');
      }
      this.el.value = formattedValue;
      this.numberValue = this.currencyFormat.parse(formattedValue);
    } else {
      this.el.value = this.numberValue = null;
    }
    this.formattedValue = this.el.value;
    this.dispatchEvent('input');
  }
  addEventListener () {
    this.el.addEventListener('input', (e) => {
      if (!e.detail) {
        const { value, selectionStart } = this.el;
        this.format(value);
        if (this.focus) {
          const getCaretPositionAfterFormat = () => {
            const { prefix, suffix, decimalSymbol, maximumFractionDigits, groupingSymbol } = this.currencyFormat;
            let caretPositionFromLeft = value.length - selectionStart;
            const newValueLength = this.formattedValue.length;
            if (this.formattedValue.substr(selectionStart, 1) === groupingSymbol && count(this.formattedValue, groupingSymbol) === count(value, groupingSymbol) + 1) {
              return newValueLength - caretPositionFromLeft - 1
            }
            if (decimalSymbol) {
              const decimalSymbolPosition = value.indexOf(decimalSymbol) + 1;
              if (Math.abs(newValueLength - value.length) > 1 && selectionStart <= decimalSymbolPosition) {
                return this.formattedValue.indexOf(decimalSymbol) + 1
              } else {
                if (!this.autoDecimalDigits && selectionStart > decimalSymbolPosition) {
                  if (this.currencyFormat.onlyDigits(value.substr(decimalSymbolPosition)).length - 1 === maximumFractionDigits) {
                    caretPositionFromLeft -= 1;
                  }
                }
              }
            }
            return this.hideCurrencySymbolOnFocus ? newValueLength - caretPositionFromLeft : Math.max(newValueLength - Math.max(caretPositionFromLeft, suffix.length), prefix.length)
          };
          this.setCaretPosition(getCaretPositionAfterFormat());
        }
      }
    }, { capture: true });
    this.el.addEventListener('focus', () => {
      this.focus = true;
      setTimeout(() => {
        const { value, selectionStart, selectionEnd } = this.el;
        if ((this.hideCurrencySymbolOnFocus || this.hideGroupingSymbolOnFocus || this.hideNegligibleDecimalDigitsOnFocus) && value) {
          this.format(value, this.hideNegligibleDecimalDigitsOnFocus);
        }
        if (Math.abs(selectionStart - selectionEnd) > 0) {
          this.setCaretPosition(0, this.el.value.length);
        } else {
          const getSelectionStart = () => {
            const { prefix, suffix, groupingSymbol } = this.currencyFormat;
            if (!this.hideCurrencySymbolOnFocus) {
              if (selectionStart > value.length - suffix.length) {
                return this.formattedValue.length - suffix.length
              } else if (selectionStart < prefix.length) {
                return prefix.length
              }
            }
            let result = selectionStart;
            if (this.hideCurrencySymbolOnFocus) {
              result -= prefix.length;
            }
            if (this.hideGroupingSymbolOnFocus) {
              result -= count(value.substring(0, selectionStart), groupingSymbol);
            }
            return result
          };
          this.setCaretPosition(getSelectionStart());
        }
      });
    });
    this.el.addEventListener('keypress', (e) => {
      if (DECIMAL_SYMBOLS.includes(e.key)) {
        this.decimalSymbolInsertedAt = this.el.selectionStart;
      }
    });
    this.el.addEventListener('blur', () => {
      this.focus = false;
      if (this.numberValue != null) {
        this.applyFixedFractionFormat(this.numberValue);
      }
    });
    this.el.addEventListener('change', (e) => {
      if (!e.detail) {
        this.dispatchEvent('change');
      }
    });
  }
  setCaretPosition (start, end = start) { this.el.setSelectionRange(start, end); }
}

const findInput = (el) => el.tagName.toLowerCase() === 'input' ? el : el.querySelector('input');
var useCurrencyInput = (options) => {
  let numberInput, input;
  const inputRef = ref(null);
  const formattedValue = ref(null);
  const instance = getCurrentInstance();
  const emit = (event, value) => isVue3 ? instance.emit(event, value) : instance.proxy.$emit(event, value);
  const lazyModel = isVue3 && (instance.attrs.modelModifiers || {}).lazy;
  const numberValue = computed(() => isVue3 ? instance.props.modelValue : instance.props.value);
  const inputEvent = isVue3 ? 'update:modelValue' : 'input';
  const changeEvent = isVue3 && lazyModel ? 'update:modelValue' : 'change';
  const hasInputEventListener = isVue3 ? (!!instance.attrs['onUpdate:modelValue'] && !lazyModel) : !!instance.proxy.$listeners[inputEvent];
  const hasChangeEventListener = isVue3 ? (lazyModel || !!instance.attrs.onChange) : !!instance.proxy.$listeners[changeEvent];
  const onInput = (e) => {
    if (e.detail) {
      if (numberValue.value !== e.detail.number) {
        emit(inputEvent, e.detail.number);
      }
      formattedValue.value = e.detail.formatted;
    }
  };
  const onChange = (e) => {
    if (e.detail) {
      emit(changeEvent, e.detail.number);
      formattedValue.value = e.detail.formatted;
    }
  };
  onMounted(() => {
    input = '$el' in inputRef.value ? findInput(inputRef.value.$el) : inputRef.value;
    if (input == null) {
      throw new Error('No input element found')
    } else {
      numberInput = new NumberInput(input, options);
      if (hasInputEventListener) {
        input.addEventListener('input', onInput);
      }
      if (hasChangeEventListener) {
        input.addEventListener('change', onChange);
      }
      numberInput.setValue(numberValue.value);
    }
  });
  onUnmounted(() => {
    if (hasInputEventListener) {
      input.removeEventListener('input', onInput);
    }
    if (hasChangeEventListener) {
      input.removeEventListener('change', onChange);
    }
  });
  return {
    inputRef,
    formattedValue,
    setValue: (value) => numberInput.setValue(value),
    setOptions: (options) => numberInput.setOptions(options)
  }
};

export default useCurrencyInput;
